// RolePreviewSheet - the role picker for "view as".
//
// Portalled to document.body, deliberately. On mobile the sidebar is
// position:fixed with a transform, and a transformed ancestor creates a
// containing block that breaks overlays - the failure that clipped every app
// overlay before #372, and that made a native <select> in the drawer refuse to
// commit a selection on iOS (#424).
//
// Presentational only: it owns no auth state and changes nothing. The caller
// decides what a pick means. That keeps this PR independently revertible.
import { createPortal } from "react-dom";
import { C } from "../lib/tokens";

export default function RolePreviewSheet({
  open, roles = [], current, realRole, onPick, onClose,
}) {
  if (!open || typeof document === "undefined" || !document.body) return null;

  const node = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 4200,
        background: C.scrim,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="View the app as another role"
        style={{
          width: "100%", maxWidth: 520,
          background: C.bgPrimary,
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          border: "1px solid " + C.borderLightSolid,
          padding: "14px 14px calc(14px + env(safe-area-inset-bottom))",
          boxShadow: (C.elevation && C.elevation.overlay) || "none",
          maxHeight: "80vh", overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>
          View as
        </div>
        <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 12 }}>
          Your practice, your account. Any role other than your own is read-only.
        </div>

        {roles.map((r) => {
          const isCurrent = r === current;
          const isReal = r === realRole;
          return (
            <button
              key={r}
              onClick={() => { if (onPick) onPick(isReal ? null : r); if (onClose) onClose(); }}
              style={{
                width: "100%", textAlign: "left", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "12px", marginBottom: 6, borderRadius: 10,
                fontSize: 14, fontFamily: "inherit",
                fontWeight: isCurrent ? 700 : 500,
                background: isCurrent ? C.infoBg : C.bgSecondary,
                color: C.textPrimary,
                border: "1px solid " + (isCurrent ? C.infoSolid : C.borderLightSolid),
              }}
            >
              <span>{r}{isReal ? " (you)" : ""}</span>
              {isCurrent
                ? <span style={{ fontSize: 11, color: C.infoFg, fontWeight: 700 }}>Viewing</span>
                : !isReal && <span style={{ fontSize: 11, color: C.textTertiary }}>read-only</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
